import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IonButton, IonContent, IonModal } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { UserState } from './UserState';
import { useSubscription } from '@meraki-internal/state';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { ProfileCollectionForm, IProfileCollectionData, IProfileLocationMode, resolveLocationMode } from './ProfileCollectionForm';
import { TERMS_URL } from './consentText';
import { Text } from '../components/Text';
import { createUseStyles } from 'react-jss';
import { UserOnboardingState } from '../onboarding/UserOnboardingState';
import { CareMapState } from '../caremap/CareMapState';
import { ProfileCheckService } from './ProfileCheckService';

const useStyles = createUseStyles({
    modal: {
        '--width': '100%',
        '--height': '100%',
        '--border-radius': '0'
    },
    ionContent: {
        height: '100%',
        display: 'flex'
    },
    contentWrapper: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        width: '100%',
        paddingTop: 60
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 500,
        width: '100%',
        padding: '40px 20px'
    },
    buttons: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 400,
        padding: '20px 0',
        gap: 8
    },
    disclosure: {
        textAlign: 'center',
        padding: '12px 0 0',
        fontSize: 12,
        color: 'var(--ion-color-step-700)'
    },
    termsLink: {
        color: 'var(--ion-color-step-700)'
    }
});

export const RequireProfileCheck: FC = () => {
    const classes = useStyles();
    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const principal = useInstance(AppPrincipal);
    const isAnonymous = isAnonymousViewer(principal);

    const orgsState = useInstance(OrganizationsState);
    const careMapState = useInstance(CareMapState);
    const profileCheck = useInstance(ProfileCheckService);

    const onboarding = useInstance(UserOnboardingState);
    useSubscription(() => onboarding);

    const [isOpen, setIsOpen] = useState(false);
    const wasSubmittedRef = useRef(false);
    const [hasName, setHasName] = useState(false);
    // "Welcome back!" greets a named family caregiver or staff member; an invitee is greeted as new
    const [isReturning, setIsReturning] = useState(false);
    // captured when the form opens, so the buttons and the consent text cannot change under a save
    const [hasTosConsent, setHasTosConsent] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [formData, setFormData] = useState<IProfileCollectionData>({
        firstName: '',
        lastName: '',
        location: '',
        phone: ''
    });
    const [isValid, setIsValid] = useState(false);
    const [locationMode, setLocationMode] = useState<IProfileLocationMode>('zip');

    useEffect(() => {
        if (isAnonymous) {
            return;
        }
        if (principal.tokenType === 'caremap-viewer') {
            return;
        }

        // the account reloads on every reconnect and return to the app; re-seeding the open form would wipe what was typed
        if (isOpen) {
            return;
        }

        // wait for onboarding state before deciding
        if (!onboarding.isLoaded()) {
            return;
        }

        // defer to Welcome if it hasn't been completed yet
        if (!onboarding.isStepComplete('recording')) {
            return;
        }

        // skip if user already dismissed or submitted this session
        if (profileCheck.wasSkippedThisSession()) {
            return;
        }
        if (wasSubmittedRef.current) {
            return;
        }

        // delay to let redirects happen (same pattern as old RequireNameModal)
        const timer = setTimeout(() => {
            const namePresent = Boolean(userState.getName());
            const hasLocation = Boolean(userState.state.address);
            const hasPhone = Boolean(userState.state.phone);
            setHasName(namePresent);
            setIsReturning(namePresent && !careMapState.isInvitee());
            setHasTosConsent(userState.state.consents?.some(c => c.consentType === 'terms-of-service') ?? false);
            if (!namePresent || !hasLocation || !hasPhone) {
                const rawPhone = userState.state.phone || '';
                const formattedPhone = rawPhone.length === 10
                    ? `${rawPhone.slice(0, 3)}-${rawPhone.slice(3, 6)}-${rawPhone.slice(6)}`
                    : rawPhone;
                setLocationMode(resolveLocationMode(userState.state.address));
                setFormData(prev => ({
                    ...prev,
                    firstName: userState.state.firstName || '',
                    lastName: userState.state.lastName || '',
                    location: userState.state.address || '',
                    phone: formattedPhone
                }));
                setIsOpen(true);
                profileCheck.trackViewed();
            }
        }, 250);

        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnonymous, principal, userState, orgsState, profileCheck, onboarding.state?.loaded, onboarding.state?.steps.recording]);

    const consentText = profileCheck.getSaveConsentText(hasTosConsent);

    const onSubmit = useCallback(async () => {
        setIsSaving(true);
        wasSubmittedRef.current = true;
        try {
            await profileCheck.save({ data: formData, hasTosConsent });
        } finally {
            setIsSaving(false);
        }
        setIsOpen(false);
    }, [profileCheck, formData, hasTosConsent]);

    const onSkip = useCallback(() => {
        profileCheck.skip();
        setIsOpen(false);
    }, [profileCheck]);

    const onAcceptTermsAndSkip = useCallback(async () => {
        setIsSaving(true);
        try {
            await profileCheck.acceptTermsAndSkip();
        } finally {
            setIsSaving(false);
        }
        setIsOpen(false);
    }, [profileCheck]);

    if (!isOpen) {
        return null;
    }

    return (
        <IonModal isOpen data-id="require-profile-overlay" backdropDismiss={false} className={classes.modal}>
            <IonContent className={classes.ionContent}>
                <div className={classes.contentWrapper}>
                <div className={classes.content}>
                    <div style={{textAlign: 'center', marginBottom: 20}}>
                        <div data-id="profile-check-title" style={{fontSize: 24, fontWeight: 'bold'}}>{isReturning ? 'Welcome back!' : 'Tell us about yourself'}</div>
                        <div style={{padding: '12px 0 0'}}>
                            <Text type="detail">Bea needs a few more details to personalize your experience.</Text>
                        </div>
                    </div>
                    <ProfileCollectionForm
                        data={formData}
                        onChange={setFormData}
                        onValidityChange={setIsValid}
                        locationMode={locationMode}
                        autoFocus
                    />
                    <div className={classes.buttons}>
                        {hasName && hasTosConsent && <IonButton fill="clear" type="button" data-id="profile-skip-button" disabled={isSaving} onClick={onSkip}>Skip for now</IonButton>}
                        {hasName && !hasTosConsent && (<>
                            <IonButton fill="clear" type="button" data-id="profile-accept-terms-and-skip-button" disabled={isSaving} onClick={onAcceptTermsAndSkip}>Accept terms and skip</IonButton>
                            <div className={classes.disclosure}>
                                {'By selecting "Accept terms and skip" you accept our '}
                                <a href={TERMS_URL} target="_blank" rel="noreferrer" className={classes.termsLink}>Terms</a>
                                {'.'}
                            </div>
                        </>)}
                        <div className={classes.disclosure}>
                            {hasTosConsent ? consentText : (<>
                                {'By selecting "Save" you accept our '}
                                <a href={TERMS_URL} target="_blank" rel="noreferrer" className={classes.termsLink}>Terms</a>
                                {' and consent to receive texts from Innerhive, including tips, updates, and offers. Msg frequency varies; data rates may apply. Text STOP to cancel.'}
                            </>)}
                        </div>
                        <IonButton expand="block" style={{'--border-radius': '16px'}} data-id="profile-save-button" disabled={!isValid || isSaving} onClick={onSubmit}>Save</IonButton>
                    </div>
                </div>
                </div>
            </IonContent>
        </IonModal>
    );
};
